export const MIN_ADULT_AGE = 18;
export const MIN_FEMALE_AGE = 18;
export const MIN_MALE_AGE = 21;
export const MAX_HUMAN_AGE = 100;
export const AGE_INELIGIBLE_MESSAGE = 'Not eligible — your age does not meet the minimum requirement.';

export function getMinimumAgeForGender(gender?: string | null): number {
  const normalized = String(gender || '').trim().toLowerCase();
  if (normalized === 'male' || normalized === 'm') {
    return MIN_MALE_AGE;
  }
  if (normalized === 'female' || normalized === 'f') {
    return MIN_FEMALE_AGE;
  }
  return MIN_ADULT_AGE;
}

export interface ParsedLocalDate {
  year: number;
  month: number; // 1-12
  day: number;   // 1-31
}

export interface DobValidationResult {
  valid: boolean;
  age: number | null;
  normalizedDob: string;
  error?: string;
}

/**
 * Formats year, month (1-12), and day (1-31) into canonical YYYY-MM-DD string
 * without any UTC timezone conversion.
 */
export function formatLocalDob(year: number, month: number, day: number): string {
  const y = String(Math.trunc(year)).padStart(4, '0');
  const m = String(Math.trunc(month)).padStart(2, '0');
  const d = String(Math.trunc(day)).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Returns the number of days in a given month (1-12) of a given year,
 * accurately accounting for leap years.
 */
export function getDaysInMonth(year: number, month: number): number {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return 31;
  }
  return new Date(year, month, 0).getDate();
}

/**
 * Parses a YYYY-MM-DD string into local { year, month, day } without UTC drift.
 * Returns null if the format or calendar date (e.g. Feb 29 on non-leap year) is invalid.
 */
export function parseLocalDob(dobString: unknown): ParsedLocalDate | null {
  if (typeof dobString !== 'string') return null;
  const trimmed = dobString.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  if (year < 1900 || month < 1 || month > 12 || day < 1) {
    return null;
  }

  const maxDay = getDaysInMonth(year, month);
  if (day > maxDay) {
    return null;
  }

  return { year, month, day };
}

function getReferenceParts(referenceDate: Date = new Date()): ParsedLocalDate {
  return {
    year: referenceDate.getFullYear(),
    month: referenceDate.getMonth() + 1,
    day: referenceDate.getDate(),
  };
}

/**
 * Returns the maximum allowed DOB (YYYY-MM-DD) for an adult user (at least 18 years old today).
 */
export function getMaxAdultDobString(referenceDate: Date = new Date()): string {
  const ref = getReferenceParts(referenceDate);
  const targetYear = ref.year - MIN_ADULT_AGE;
  const clampedDay = Math.min(ref.day, getDaysInMonth(targetYear, ref.month));
  return formatLocalDob(targetYear, ref.month, clampedDay);
}

/**
 * Returns the minimum allowed DOB (YYYY-MM-DD) (100 years ago today).
 */
export function getMinDobString(referenceDate: Date = new Date()): string {
  const ref = getReferenceParts(referenceDate);
  const targetYear = ref.year - MAX_HUMAN_AGE;
  const clampedDay = Math.min(ref.day, getDaysInMonth(targetYear, ref.month));
  return formatLocalDob(targetYear, ref.month, clampedDay);
}

/**
 * Calculates exact completed years of age from a YYYY-MM-DD string in local time.
 * Returns null if the DOB string is invalid.
 */
export function calculateAgeFromDob(dobString: unknown, referenceDate: Date = new Date()): number | null {
  const parsed = parseLocalDob(dobString);
  if (!parsed) return null;

  const ref = getReferenceParts(referenceDate);
  let age = ref.year - parsed.year;
  if (ref.month < parsed.month || (ref.month === parsed.month && ref.day < parsed.day)) {
    age -= 1;
  }
  return age;
}

/**
 * Validates that a Date of Birth string is present, a valid calendar date,
 * not in the future, and meets the strict adult age policy (18+ default,
 * or gender-specific: Male >= 21 years, Female >= 18 years).
 */
export function validateAdultDob(
  dobString: unknown,
  referenceDate: Date = new Date(),
  gender?: string | null
): DobValidationResult {
  if (typeof dobString !== 'string' || !dobString.trim()) {
    return {
      valid: false,
      age: null,
      normalizedDob: '',
      error: 'Date of Birth is required.',
    };
  }

  const parsed = parseLocalDob(dobString);
  if (!parsed) {
    return {
      valid: false,
      age: null,
      normalizedDob: '',
      error: 'Please select a valid calendar Date of Birth (YYYY-MM-DD).',
    };
  }

  const normalizedDob = formatLocalDob(parsed.year, parsed.month, parsed.day);
  const ref = getReferenceParts(referenceDate);

  const isFuture =
    parsed.year > ref.year ||
    (parsed.year === ref.year && parsed.month > ref.month) ||
    (parsed.year === ref.year && parsed.month === ref.month && parsed.day > ref.day);

  if (isFuture) {
    return {
      valid: false,
      age: null,
      normalizedDob,
      error: 'Date of Birth cannot be in the future.',
    };
  }

  const age = calculateAgeFromDob(normalizedDob, referenceDate);
  if (age === null) {
    return {
      valid: false,
      age: null,
      normalizedDob: '',
      error: 'Please select a valid Date of Birth.',
    };
  }

  const minRequiredAge = getMinimumAgeForGender(gender);
  const hasExplicitGender = typeof gender === 'string' && gender.trim().length > 0;

  if (age < minRequiredAge) {
    return {
      valid: false,
      age,
      normalizedDob,
      error: hasExplicitGender
        ? AGE_INELIGIBLE_MESSAGE
        : `You must be at least ${MIN_ADULT_AGE} years old to register on PairUpToday.`,
    };
  }

  if (age > MAX_HUMAN_AGE) {
    return {
      valid: false,
      age,
      normalizedDob,
      error: `Please enter a valid Date of Birth (between ${minRequiredAge} and ${MAX_HUMAN_AGE} years old).`,
    };
  }

  return {
    valid: true,
    age,
    normalizedDob,
  };
}

/**
 * Validates Date of Birth against the gender-specific age eligibility rules:
 * - Male applicants: minimum 21 years
 * - Female applicants: minimum 18 years
 * Returns exact error "Not eligible — your age does not meet the minimum requirement." when ineligible.
 */
export function validateGenderAgeEligibility(
  dobString: unknown,
  gender: string,
  referenceDate: Date = new Date()
): DobValidationResult {
  const normalizedGender = String(gender || '').trim().toLowerCase();
  if (normalizedGender !== 'male' && normalizedGender !== 'female') {
    return {
      valid: false,
      age: calculateAgeFromDob(dobString, referenceDate),
      normalizedDob: typeof dobString === 'string' ? dobString.trim() : '',
      error: 'Please select a valid gender (Male or Female).',
    };
  }
  return validateAdultDob(dobString, referenceDate, gender);
}
